import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppShell from '../components/AppShell';
import Toast from '../components/Toast';
import DepositSheet from '../components/DepositSheet';
import WithdrawSheet from '../components/WithdrawSheet';
import WalletHistory from '../components/WalletHistory';
import api from '../api';
import { isSqrtClan, fmtBits } from '../lib/clan';
import { formatPts, formatCount } from '../lib/format';
import { sqrtRevenueTo } from '../lib/curve';

/**
 * 07 — Wallet (Step 5).
 * Reference: /app/reference/07-portfolio-wallet.html
 *
 * REAL:
 *   - Points balance (live)
 *   - Holdings tab — per-Clan cards with current value, avg buy, P&L $ and %
 *   - Activity tab — full personal transaction history from /wallet/activity
 *   - Deposit / Withdraw sheets (NowPayments crypto deposit + manual admin withdrawal)
 */

const CATEGORY_COLOR = {
  'Trader': '#FF5722', 'Content Creator': '#E040FB', 'Coach': '#00B4FF',
  'Educator': '#B4FF00', 'Community Builder': '#00B4FF', 'Influencer': '#FFC107',
  'Creator': '#E040FB',
};

function timeAgo(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// Phase 1 P0 — microsecond-precision arrival timestamp. Rendered on
// buy/sell rows so users can see exact FIFO ordering and prove the batch
// engine didn't front-run them. Same formatter as CreatorProfile.jsx.
function fmtArrivedUs(us) {
  if (!us || typeof us !== 'number') return null;
  const ms = Math.floor(us / 1000);
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  const micros = String(us % 1_000_000).padStart(6, '0');
  return `${hh}:${mm}:${ss}.${micros}`;
}

const ACTIVITY_ICON = {
  buy: { cls: 'buy', path: <path d="M8 12V4M4 8l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /> },
  sell: { cls: 'sell', path: <path d="M8 4v8M4 8l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /> },
  referral: { cls: 'referral', path: <path d="M4 8h8M8 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /> },
  royalty: { cls: 'referral', path: <path d="M8 2l1.7 3.4L13.5 6l-2.7 2.6.6 3.7L8 10.6 4.6 12.3l.6-3.7L2.5 6l3.8-.6L8 2z" fill="currentColor" /> },
  perk: { cls: 'referral', path: <path d="M3 6h10v7H3zM8 3v3M6 3v3M10 3v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /> },
  deposit: { cls: 'deposit', path: <path d="M8 3v10M3 8l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /> },
  withdraw: { cls: 'withdraw', path: <path d="M8 13V3M3 8l5-5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /> },
};

export default function Wallet() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sp] = useSearchParams();
  const [portfolio, setPortfolio] = useState(null); // null → loading
  const [activity, setActivity] = useState(null);
  const [stash, setStash] = useState(null); // Phase 9.5 — read-only earnings tracker
  const [tab, setTab] = useState('history');
  const [toast, setToast] = useState(null);
  const [depositOpen, setDepositOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [expandedHolding, setExpandedHolding] = useState(null); // handle of the currently expanded row

  // Locale-aware formatters with thousands separators. `money2` returns
  // "301,159.11", `money1` returns "225.8". Both hop through the shared
  // formatPts helper so grouping stays consistent with the rest of the
  // app (History tab, Trade page, etc.) and respects the current i18n
  // locale (US: `,` grouping · DE: `.` grouping · etc.).
  const money2 = (n) => formatPts(n, 2);
  const money1 = (n) => formatPts(n, 1);

  const refreshAll = () => {
    api.get('/wallet/portfolio')
      .then(({ data }) => setPortfolio(data))
      .catch(() => setPortfolio({ balance: 0, holdings: [], portfolio_value: 0, portfolio_cost: 0, portfolio_pnl_abs: 0, portfolio_pnl_pct: 0 }));
    api.get('/wallet/activity')
      .then(({ data }) => setActivity(data.events || []))
      .catch(() => setActivity([]));
    api.get('/wallet/stash')
      .then(({ data }) => setStash(data))
      .catch(() => setStash(null));
  };

  useEffect(() => {
    refreshAll();
    // If arriving from a successful sandbox checkout (?deposit=id) show a toast
    if (sp.get('deposit')) {
      setToast({ msg: t('wallet.depositReceived') });
      setTab('activity');
    }
    // Deep-link from other surfaces (e.g. the "Add balance to buy more" CTA on
    // TradingSheet drops the user here with ?openDeposit=1) — auto-open the
    // Deposit sheet so the user lands directly on the amount input.
    if (sp.get('openDeposit')) {
      setDepositOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showComingSoon = (label) => setToast({ msg: `🚧 ${label} — coming soon.` });

  if (!portfolio) {
    return (
      <AppShell activeTab="wallet">
        <div className="wallet-wrap"><div className="loading-shell">{t('wallet.loading')}</div></div>
      </AppShell>
    );
  }

  const up = (portfolio.portfolio_pnl_net ?? portfolio.portfolio_pnl_abs) >= 0;

  return (
    <AppShell activeTab="wallet">
      <div className="wallet-wrap" data-testid="wallet-page">
        <div className="wallet-grid">
          {/* SIDEBAR */}
          <aside className="wallet-sidebar">
            {/* Balance hero */}
            <div className="balance-hero" data-testid="wallet-balance-hero">
              <div className="balance-label">{t('wallet.pointsBalance')}</div>
              <div className="balance-value" data-testid="wallet-balance-value">{money2(portfolio.balance)}</div>
              <div className="balance-usd">{t('wallet.usdApprox', { amount: money2(portfolio.balance) })}</div>
              <div className="balance-actions">
                <button className="bh-btn primary" onClick={() => setDepositOpen(true)} data-testid="wallet-deposit-btn">
                  <svg viewBox="0 0 16 16" fill="none" width="15" height="15">
                    <path d="M8 3v10M3 8l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {t('wallet.deposit')}
                </button>
                <button className="bh-btn secondary" onClick={() => setWithdrawOpen(true)} data-testid="wallet-withdraw-btn">
                  <svg viewBox="0 0 16 16" fill="none" width="15" height="15">
                    <path d="M8 13V3M3 8l5-5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {t('wallet.withdraw')}
                </button>
              </div>
            </div>

            {/* Portfolio value */}
            <div className="pv-card" data-testid="wallet-portfolio-card">
              <div className="pv-label" title={t('wallet.portfolioValueTitle')}>
                {t('wallet.portfolioValue')}
              </div>
              <div className="pv-value" data-testid="wallet-portfolio-value">{money2(portfolio.portfolio_value)} {t('common.pts')}</div>
              <div className={`pv-change ${up ? 'up' : 'down'}`} data-testid="wallet-portfolio-pnl">
                <svg viewBox="0 0 12 12" fill="none">
                  {up
                    ? <path d="M2 8l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    : <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
                </svg>
                {up ? '+' : '−'}{money2(Math.abs(portfolio.portfolio_pnl_net ?? portfolio.portfolio_pnl_abs))} {t('common.pts')} ({money1(Math.abs(portfolio.portfolio_pnl_net_pct ?? portfolio.portfolio_pnl_pct))}%)
              </div>
              <div className="pv-cost-note">
                {t('wallet.portfolioCost', { cost: money2(portfolio.portfolio_gross_paid ?? portfolio.portfolio_cost), count: portfolio.holdings.length })}
              </div>
            </div>

            {/* Portfolio scenarios block removed Feb 2026 — replaced by
                per-Clan growth projections inside the History tab. Each
                clan card now shows its OWN "if it grows to 1K/10K/100K"
                projection when expanded, which is far more actionable
                than one aggregate number that mixes bull and bear clans. */}

            {/* Phase 9.5 — Stash (read-only earnings tracker). Always visible
                once stash data has loaded, even at zero totals — a persistent
                dashboard slot so users know earnings will show up here. */}
            {stash && (
              <div className="stash-card" data-testid="wallet-stash-card">
                <div className="stash-header">
                  <span className="stash-emoji">💰</span>
                  <span className="stash-title">{t('wallet.stashTitle')}</span>
                </div>
                <div className="stash-hero">
                  <div className="stash-hero-value" data-testid="stash-lifetime">
                    +{money2(stash.lifetime.total)}
                    <span className="stash-hero-unit"> {t('common.pts')}</span>
                  </div>
                  <div className="stash-hero-label">{t('wallet.stashLifetime')}</div>
                </div>
                <div className="stash-month">
                  <span className="stash-month-label">
                    {stash.month_label}
                  </span>
                  <span className="stash-month-value" data-testid="stash-month">
                    +{money2(stash.month.total)} {t('common.pts')}
                  </span>
                </div>
                <div className="stash-breakdown">
                  <div className="stash-row">
                    <span className="stash-row-label">
                      <span className="stash-row-dot" style={{ background: '#00B4FF' }} />
                      {t('wallet.stashRoyalties')}
                    </span>
                    <span className="stash-row-value" data-testid="stash-royalties">
                      +{money2(stash.lifetime.royalties.total)} {t('common.pts')}
                      <span className="stash-row-count">
                        {t('wallet.tradesCount', { count: stash.lifetime.royalties.count, countStr: formatCount(stash.lifetime.royalties.count) })}
                      </span>
                    </span>
                  </div>
                  <div className="stash-row">
                    <span className="stash-row-label">
                      <span className="stash-row-dot" style={{ background: '#E040FB' }} />
                      {t('wallet.stashReferrals')}
                    </span>
                    <span className="stash-row-value" data-testid="stash-referrals">
                      +{money2(stash.lifetime.referrals.total)} {t('common.pts')}
                      <span className="stash-row-count">
                        {t('wallet.tradesCount', { count: stash.lifetime.referrals.count, countStr: formatCount(stash.lifetime.referrals.count) })}
                      </span>
                    </span>
                  </div>
                  {stash.lifetime.perks && (
                    <div className="stash-row" data-testid="stash-perks-row">
                      <span className="stash-row-label">
                        <span className="stash-row-dot" style={{ background: '#4ade80' }} />
                        {t('wallet.stashPerks')}
                      </span>
                      <span className="stash-row-value" data-testid="stash-perks">
                        {stash.lifetime.perks.total >= 0 ? '+' : ''}{money2(stash.lifetime.perks.total)} {t('common.pts')}
                        <span className="stash-row-count">
                          {t('wallet.redeemsCount', { count: stash.lifetime.perks.count, countStr: formatCount(stash.lifetime.perks.count) })}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
                <div className="stash-hint">
                  {stash.lifetime.total > 0
                    ? t('wallet.stashHintNonZero')
                    : t('wallet.stashHintZero')}
                </div>
              </div>
            )}
          </aside>

          {/* MAIN */}
          <main className="wallet-main">
            <div className="wallet-tabs" role="tablist">
              <button
                className={`wallet-tab${tab === 'history' ? ' active' : ''}`}
                onClick={() => setTab('history')}
                role="tab"
                aria-selected={tab === 'history'}
                data-testid="wallet-tab-history"
              >
                Portfolio{portfolio.holdings.length > 0 ? ` · ${portfolio.holdings.length}` : ''}
              </button>
              <button
                className={`wallet-tab${tab === 'activity' ? ' active' : ''}`}
                onClick={() => setTab('activity')}
                role="tab"
                aria-selected={tab === 'activity'}
                data-testid="wallet-tab-activity"
              >
                {t('wallet.tabActivity')}{activity && activity.length ? ` · ${activity.length}` : ''}
              </button>
            </div>

            {/* Holdings tab removed Feb 2026 (Option A merge) — the History
                tab is now the single "Portfolio" surface. It absorbs the
                projected sellback tiers + Buy/Sell/View action buttons that
                used to live here, and gains realized/unrealized split +
                filters + individual trade rows that Holdings never had. */}

            {tab === 'history' && <WalletHistory />}

            {tab === 'activity' && (
              <div className="wallet-panel" data-testid="wallet-panel-activity">
                {activity === null && <div className="loading-shell" style={{ minHeight: 100 }}>{t('wallet.loadingActivity')}</div>}
                {activity !== null && activity.length === 0 && (
                  <div className="wallet-empty" data-testid="wallet-activity-empty">
                    <div className="we-icon">🧾</div>
                    <h3>{t('wallet.activityEmptyTitle')}</h3>
                    <p>{t('wallet.activityEmptyBody')}</p>
                  </div>
                )}
                {activity !== null && activity.length > 0 && (
                  <div className="wallet-activity">
                    {activity.map((ev, i) => {
                      const icon = ACTIVITY_ICON[ev.kind] || ACTIVITY_ICON.buy;
                      const positive = ev.amount >= 0;
                      const arrivedFmt = (ev.kind === 'buy' || ev.kind === 'sell')
                        ? fmtArrivedUs(ev.arrived_at_us)
                        : null;
                      return (
                        <div key={i} className="activity-card" data-kind={ev.kind || 'buy'} data-testid={`activity-${i}`}>
                          <div className={`a-icon ${icon.cls}`} aria-hidden="true">
                            <svg viewBox="0 0 16 16" fill="none" width="16" height="16">
                              {icon.path}
                            </svg>
                          </div>
                          <div className="a-info">
                            <div className="a-title">
                              {ev.title}
                              {ev.subtitle && <span className="a-subtitle"> · {ev.subtitle}</span>}
                            </div>
                            <div className="a-time">
                              {timeAgo(ev.created_at)}
                              {arrivedFmt && (
                                <span
                                  className="cp-activity-us"
                                  data-testid={`activity-us-${i}`}
                                  title={t('wallet.arrivedTitle')}
                                >
                                  {arrivedFmt}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="a-right">
                            <div className={`a-amount ${positive ? 'pos' : 'neg'}`} data-testid={`activity-amount-${i}`}>
                              {positive ? '+' : '−'}{Math.abs(ev.amount).toFixed(2)} {t('common.pts')}
                            </div>
                            <span className={`a-status ${ev.status || 'completed'}`}>{ev.status || 'completed'}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </main>
        </div>
      </div>

      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}
      {depositOpen && (
        <DepositSheet
          onClose={() => setDepositOpen(false)}
          onDeposited={() => {
            setDepositOpen(false);
            refreshAll();
            setTab('activity');
            setToast({ msg: t('wallet.depositReceived') });
          }}
        />
      )}
      {withdrawOpen && (
        <WithdrawSheet
          balance={portfolio.balance}
          onClose={() => setWithdrawOpen(false)}
          onWithdrawn={(tx) => {
            setWithdrawOpen(false);
            refreshAll();
            setTab('activity');
            setToast({ msg: t('wallet.withdrawalSent', { status: tx.status }) });
          }}
        />
      )}
    </AppShell>
  );
}
